import { useState } from "react";

const processSteps = [
  { num: "1", icon: "ri-send-plane-fill", label: "Submit Form", desc: "Fill out your details and describe the issue" },
  { num: "2", icon: "ri-user-search-fill", label: "Engineer Reviews", desc: "Our specialist analyzes your case within 24hrs" },
  { num: "3", icon: "ri-calendar-check-fill", label: "Inspection Scheduled", desc: "We coordinate a convenient time for your site" },
  { num: "4", icon: "ri-file-list-3-fill", label: "Recommendation Delivered", desc: "You receive a clear technical report — no obligation" },
];

export default function ContactFormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) data.append(input.name, input.value);
    });
    try {
      await fetch("https://readdy.ai/api/form/d7f5b695vm7vckkodu20", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#f0f4f8] py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-[#4a7fa5] font-bold uppercase tracking-widest text-sm mb-3">Free Technical Inspection</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0a0e1a] max-w-3xl mx-auto leading-tight">
            Schedule a Free{" "}
            <span className="text-[#1a3a5c]">Technical Inspection</span>
          </h2>
          <p className="text-gray-400 text-xs mt-4 uppercase tracking-widest font-semibold">
            No Obligation. &mdash; No Guesswork. &mdash; Just Technical Clarity.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

          {/* Left — Photo + contact info + process */}
          <div className="flex flex-col gap-8">
            {/* Photo */}
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://clwizard.com/wp-content/uploads/2024/03/IMG_0599_Edit2b.jpg"
                alt="CLWizard engineer on site"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Contact info */}
            <div className="flex flex-col gap-3">
              <a
                href="tel:5617745120"
                className="flex items-center gap-3 text-[#0a0e1a] font-bold text-base hover:text-[#1a3a5c] transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1a3a5c]/10">
                  <i className="ri-phone-fill text-[#4a7fa5] text-lg"></i>
                </span>
                (561) 774-5120
              </a>
              <a
                href="mailto:info@clwizard.com"
                className="flex items-center gap-3 text-[#0a0e1a] font-bold text-base hover:text-[#1a3a5c] transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1a3a5c]/10">
                  <i className="ri-mail-fill text-[#4a7fa5] text-lg"></i>
                </span>
                info@clwizard.com
              </a>
            </div>

            {/* 4-step process */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#0a0e1a] font-extrabold text-sm uppercase tracking-wider mb-5">What Happens Next</p>
              <div className="flex flex-col gap-5">
                {processSteps.map((step, idx) => (
                  <div key={step.num} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1a3a5c] flex-shrink-0">
                        <i className={`${step.icon} text-white text-sm`}></i>
                      </div>
                      {idx < processSteps.length - 1 && (
                        <div className="w-0.5 h-5 bg-[#1a3a5c]/20 mt-1"></div>
                      )}
                    </div>
                    <div className="pt-1">
                      <p className="text-[#0a0e1a] font-bold text-sm">{step.label}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1a3a5c]/10 mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-[#4a7fa5] text-3xl"></i>
                </div>
                <h4 className="text-[#0a0e1a] font-extrabold text-xl mb-2">Request Received!</h4>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Our engineering team will review your case and contact you within 24 hours to schedule your free inspection.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#0a0e1a] text-xs font-bold mb-1.5 block uppercase tracking-wide">Full Name *</label>
                    <input
                      name="full_name"
                      type="text"
                      required
                      placeholder="John Smith"
                      className="w-full border border-gray-200 text-[#0a0e1a] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-400 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="text-[#0a0e1a] text-xs font-bold mb-1.5 block uppercase tracking-wide">Phone *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="(561) 000-0000"
                      className="w-full border border-gray-200 text-[#0a0e1a] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-400 bg-gray-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[#0a0e1a] text-xs font-bold mb-1.5 block uppercase tracking-wide">Email *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="w-full border border-gray-200 text-[#0a0e1a] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-400 bg-gray-50"
                  />
                </div>

                <div>
                  <label className="text-[#0a0e1a] text-xs font-bold mb-1.5 block uppercase tracking-wide">Property Type *</label>
                  <select
                    name="property_type"
                    required
                    className="w-full border border-gray-200 text-[#0a0e1a] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#4a7fa5] bg-gray-50"
                  >
                    <option value="">Select property type</option>
                    <option value="Condominium">Condominium</option>
                    <option value="Commercial Building">Commercial Building</option>
                    <option value="Hotel">Hotel</option>
                    <option value="Parking Garage">Parking Garage</option>
                    <option value="HOA Community">HOA Community</option>
                    <option value="Office Building">Office Building</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-[#0a0e1a] text-xs font-bold mb-1.5 block uppercase tracking-wide">
                    Message <span className="text-gray-400 font-normal normal-case">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="Describe the leak issue, location, and any previous repair attempts..."
                    className="w-full border border-gray-200 text-[#0a0e1a] text-sm px-4 py-3 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-400 bg-gray-50 resize-none"
                  ></textarea>
                  {charCount > 500 && (
                    <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="bg-[#1a3a5c] hover:bg-[#0f2a45] disabled:opacity-60 text-white font-extrabold px-6 py-4 rounded-md transition-colors whitespace-nowrap text-sm"
                >
                  {submitting ? "Sending..." : "Request My Free Engineering Assessment"}
                </button>

                <p className="text-gray-400 text-xs text-center">
                  No obligation. No guesswork. Our engineer will contact you within 24 hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
